import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Space, Button, Col, Row, Modal, Drawer, Tag, Tooltip } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  // DeleteOutlined,
  EyeOutlined,
  CloseOutlined,
  UploadOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
  FileSearchOutlined,
  CopyOutlined,
  // FileAddOutlined,
} from "@ant-design/icons";
import { BiFilterAlt } from "react-icons/bi";
import { useForm } from "antd/lib/form/Form";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import { RootState } from "../../shared/types/common";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import ActivityLogComponent from "../../shared/components/activity-log/activity-log.component";
import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";
import { isFilterValueActive } from "../../shared/utils/active-filters";
import { accessAllowed, showNotification } from "../../shared/utils/utils";
import { allPermissions } from "../../shared/constants/constants";
import { ROUTE_SCREENING_RESULTS } from "../../shared/routes/private.routes";
import {
  deleteJobDescription,
  getJobDescriptionList,
} from "../../core/store/reducers/candidate-screening.slice";
import CreateEditJobDescriptionComponent from "./components/create-edit-job-description.component";
// import BulkUploadJobDescriptionsComponent from "./components/bulk-upload-job-descriptions.component";
import UploadCandidatesComponent from "./components/upload-candidates.component";
import RunScreeningComponent from "./components/run-screening.component";

const JD_FILTER_LABELS: Record<string, string> = {
  title: "Title",
  status: "Status",
};

function jdFilterValueText(_key: string, value: any): string {
  return Array.isArray(value) ? value.join(", ") : String(value);
}

const JD_FILTER_CLEAR: Record<string, any> = {
  title: undefined,
  status: [],
};

const ManageJobDescriptionPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = useForm();
  const { loadingJd, jdList, jdPagination } = useSelector(
    (state: RootState) => state.candidateScreening
  );
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const { screenType } = useSelector((state: RootState) => state.settings);

  const [jdListData, setJdListData] = useState<any[]>([]);
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<any>(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [sorter, setSorter] = useState({ sort_by: "", sort_type: "" });
  const [filters, setFilters] = useState<any>({});
  const [openLogs, setOpenLogs] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [jdIdToDelete, setJdIdToDelete] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  // const [bulkJdOpen, setBulkJdOpen] = useState(false);
  const [runScreeningOpen, setRunScreeningOpen] = useState(false);

  const loadList = (page: any = null) => {
    const data = {
      page: page ? page : jdPagination?.page ? jdPagination?.page : 1,
      limit: jdPagination?.limit || TABLE_ROWS_COUNT,
      ...sorter,
    };
    const filter: any = {};
    Object.keys(filters)?.forEach((val: any) => {
      if (filters[val]) filter[val] = filters[val];
    });
    dispatch(getJobDescriptionList({ params: { ...data, ...filter } }));
  };

  useEffect(() => {
    loadList();
  }, [filters, sorter]);

  useEffect(() => {
    setJdListData(
      (jdList || []).map((obj: any, index: number) => ({
        key: index,
        ...obj,
      }))
    );
  }, [jdList]);

  const createEntityListner = () => {
    setSelectedEntity(null);
    setCreateEditOpen(true);
  };
  const editEntityListner = (entity: any) => {
    setSelectedEntity(entity);
    setCreateEditOpen(true);
  };
  const copyEntityListner = (entity: any) => {
    setSelectedEntity({
      ...entity,
      id: undefined,
      serialNumber: undefined,
      candidateCount: 0,
      hasScreeningResults: false,
      isCopy: true,
    });
    setCreateEditOpen(true);
  };
  const handleOpenLogs = (record: any) => {
    setSelectedEntity(record);
    setOpenLogs(true);
  };
  const handleRunScreening = (record: any) => {
    setSelectedEntity(record);
    setRunScreeningOpen(true);
  };
  const handleUploadResumes = (record: any) => {
    setSelectedEntity(record);
    setUploadOpen(true);
  };
  const handleViewResults = (record: any) => {
    navigate(`${ROUTE_SCREENING_RESULTS}/${record?.id}`);
  };

  const onFilterApply = (values: any) => setFilters(values);

  const activeFilterEntries = useMemo(() => {
    return Object.keys(filters)
      .filter((k) => isFilterValueActive(filters[k]))
      .map((key) => ({
        key,
        fieldLabel: JD_FILTER_LABELS[key] || key,
        valueText: jdFilterValueText(key, filters[key]),
      }));
  }, [filters]);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    form.resetFields();
  }, [form]);

  const removeFilter = useCallback(
    (key: string) => {
      setFilters((prev: any) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      const cleared =
        key in JD_FILTER_CLEAR ? JD_FILTER_CLEAR[key] : undefined;
      form.setFieldsValue({ [key]: cleared });
    },
    [form]
  );

  const onPageChange = (pageNumber: number) => loadList(pageNumber);

  const onChangeHandler = (_p: any, _f: any, _sorter: any) => {
    if (_sorter.order === undefined) {
      setSorter({ sort_by: "", sort_type: "" });
    } else {
      const order = _sorter.order === "ascend" ? "asc" : "desc";
      setSorter({ sort_by: _sorter.columnKey, sort_type: order });
    }
  };

  const handleDelete = (id: string) => {
    dispatch(deleteJobDescription(id)).then((val: any) => {
      if (val?.payload?.data) {
        showNotification("Job Description Deleted Successfully", "success");
        loadList();
        setDeleteModalVisible(false);
        setJdIdToDelete("");
      }
    });
  };

  const canCreateJd = accessAllowed(myPermissions, allPermissions.JD_MASTER_CREATE);
  // const canBulkCreateJd = accessAllowed(myPermissions, allPermissions.JD_MASTER_BULK_CREATE);
  const canUpdateJd = accessAllowed(myPermissions, allPermissions.JD_MASTER_UPDATE);
  const canRunScreening = accessAllowed(myPermissions, allPermissions.JD_MASTER_SCREENING_RUN);
  const canViewRank = accessAllowed(myPermissions, allPermissions.JD_MASTER_RANK);
  const canUploadResumes = accessAllowed(myPermissions, allPermissions.JD_MASTER_RESUME_UPLOAD);
  const canViewLogs = accessAllowed(myPermissions, allPermissions.JD_MASTER_ACTIVITY_LOGS);
  const hasAnyRowAccess =
    canViewRank ||
    canRunScreening ||
    canUpdateJd ||
    canViewLogs ||
    canUploadResumes;

  const columns = [
    {
      title: "JD No",
      dataIndex: "serialNumber",
      key: "serialNumber",
      sorter: false,
      render: (serialNumber: string) => serialNumber || "-",
    },
    { title: "Title", dataIndex: "title", key: "title", sorter: false },
    {
      title: "Candidates",
      dataIndex: "candidateCount",
      key: "candidateCount",
      render: (count: number) => count ?? 0,
    },
    {
      title: "Created By",
      dataIndex: "createdByName",
      key: "createdBy",
      render: (_createdByName: string, record: any) =>
        record?.createdByName || record?.createdBy || "-",
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      render: (createdDate: string) =>
        createdDate ? moment(createdDate).format("DD-MMM-YYYY") : "-",
    },
    {
      title: "Last Assessed Timestamp",
      dataIndex: "lastAssessedDate",
      key: "lastAssessedDate",
      render: (lastAssessedDate: string) =>
        lastAssessedDate ? moment(lastAssessedDate).format("DD-MMM-YYYY hh:mm A") : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={status === "ACTIVE" ? "green" : "default"}>
          {status || "—"}
        </Tag>
      ),
    },
    ...(hasAnyRowAccess
      ? [
          {
            title: "Actions",
            key: "actions",
            fixed: "right",
            render: (_text: any, record: any) => (
              <Space>
                <Tooltip title="View ranked results">
                  <Button
                    type="link"
                    onClick={() => handleViewResults(record)}
                    icon={<BarChartOutlined />}
                    hidden={!canViewRank}
                  />
                </Tooltip>
                <Tooltip title="Run screening">
                  <Button
                    type="link"
                    onClick={() => handleRunScreening(record)}
                    icon={<ThunderboltOutlined />}
                    hidden={!canRunScreening}
                  />
                </Tooltip>
                <Tooltip title="Edit">
                  <Button
                    type="link"
                    onClick={() => editEntityListner(record)}
                    icon={<EditOutlined />}
                    hidden={!canUpdateJd}
                  />
                </Tooltip>
                <Tooltip title="Clone JD">
                  <Button
                    type="link"
                    onClick={() => copyEntityListner(record)}
                    icon={<CopyOutlined />}
                    hidden={!canCreateJd}
                  />
                </Tooltip>
                {/* Delete action hidden for JD as requested.
                <Tooltip title="Delete">
                  <Button
                    type="link"
                    onClick={() => {
                      setJdIdToDelete(record?.id);
                      setDeleteModalVisible(true);
                    }}
                    icon={<DeleteOutlined style={{ color: "#FF0000" }} />}
                  />
                </Tooltip>
                */}
                <Tooltip title="Activity log">
                  <Button
                    type="link"
                    onClick={() => handleOpenLogs(record)}
                    icon={<EyeOutlined />}
                    hidden={!canViewLogs}
                  />
                </Tooltip>
                <Tooltip title="Upload resumes">
                  <Button
                    type="link"
                    onClick={() => handleUploadResumes(record)}
                    icon={<UploadOutlined />}
                    hidden={!canUploadResumes}
                  />
                </Tooltip>
              </Space>
            ),
          },
        ]
      : []),
  ];

  const formProps = {
    form: form,
    name: "manage-jd-filters",
    enableEnterSubmit: true,
    formElements: [
      {
        name: "title",
        label: "",
        placeholder: "Search by title",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
      {
        name: "status",
        label: "",
        placeholder: "Status",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: [
          { value: "ACTIVE", label: "ACTIVE" },
          { value: "INACTIVE", label: "INACTIVE" },
        ],
      },
    ],
    initialValues: {},
    extraButtons: [
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
        onClick: clearAllFilters,
      },
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Apply",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFilterApply,
    onFinishFailed: null,
    onValuesChange: () => {
      return;
    },
  };

  const headerButtons = (
    <Space size="middle" className="app-page-actions">
      {/* {canBulkCreateJd && (
        <Button icon={<FileAddOutlined />} onClick={() => setBulkJdOpen(true)}>
          <span className="font-size-14">
            {!screenType?.isPortrait && "Bulk Upload JD"}
          </span>
        </Button>
      )} */}
      {canCreateJd && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={createEntityListner}
        >
          <span className="font-size-14">
            New {!screenType?.isPortrait && "Job Description"}
          </span>
        </Button>
      )}
      <Button
        shape="circle"
        type="primary"
        icon={<BiFilterAlt />}
        onClick={() => setFilterVisible(!filterVisible)}
        title={filterVisible ? "Hide filters" : "Show filters"}
      />
    </Space>
  );

  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <FileSearchOutlined />
              </div>
              <h1 className="app-page-title">Candidate Screening</h1>
            </div>
            <p className="app-page-subtitle">
              Manage job descriptions, upload resumes, and run AI screening
            </p>
          </Col>
          <Col>{headerButtons}</Col>
        </Row>
      </div>

      {filterVisible && (
        <div className="app-filter-bar">
          <div className="app-filter-header">
            <div className="app-filter-title">
              <BiFilterAlt style={{ fontSize: 14 }} />
              Filters
            </div>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setFilterVisible(false)}
              title="Close filters"
              aria-label="Close filters"
            />
          </div>
          <div className="table-filter-form">
            <GenericFormComponent {...formProps} />
          </div>
        </div>
      )}

      <ActiveFilterChipsBar
        show={!filterVisible}
        entries={activeFilterEntries}
        onRemove={removeFilter}
        onClearAll={clearAllFilters}
      />

      <div className="app-table-card">
        <DataTableComponent
          pagination={true}
          loading={loadingJd}
          dataSource={jdListData}
          columns={columns}
          onChange={onChangeHandler}
          defaultPage={0}
          currentPage={jdPagination.page}
          totalRows={jdPagination?.total}
          onPageChange={onPageChange}
          tableWidth="max-content"
        />
      </div>

      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={() => handleDelete(jdIdToDelete)}
        onCancel={() => {
          setDeleteModalVisible(false);
          setJdIdToDelete("");
        }}
      >
        <p>Are you sure you want to delete this job description?</p>
      </Modal>

      {createEditOpen && (
        <CreateEditJobDescriptionComponent
          onClose={() => setCreateEditOpen(false)}
          visible={createEditOpen}
          selectedEntity={selectedEntity}
          onSubmit={() => {
            setCreateEditOpen(false);
            loadList();
          }}
        />
      )}

      {uploadOpen && (
        <UploadCandidatesComponent
          onClose={() => setUploadOpen(false)}
          visible={uploadOpen}
          onSubmit={() => {
            setUploadOpen(false);
            loadList();
            if (selectedEntity?.id) {
              setRunScreeningOpen(true);
            }
          }}
        />
      )}

      {/* {bulkJdOpen && (
        <BulkUploadJobDescriptionsComponent
          onClose={() => setBulkJdOpen(false)}
          visible={bulkJdOpen}
          onSubmit={() => loadList()}
        />
      )} */}

      {runScreeningOpen && (
        <RunScreeningComponent
          onClose={() => setRunScreeningOpen(false)}
          visible={runScreeningOpen}
          jobDescription={selectedEntity}
          onSubmit={() => {
            setRunScreeningOpen(false);
            if (selectedEntity?.id) {
              navigate(`${ROUTE_SCREENING_RESULTS}/${selectedEntity.id}`);
            }
          }}
        />
      )}

      {openLogs && (
        <Drawer
          open={openLogs}
          onClose={() => setOpenLogs(false)}
          title="Log Details"
        >
          <ActivityLogComponent
            id={selectedEntity?.id}
            record={selectedEntity}
            module="Job Description"
          />
        </Drawer>
      )}
    </div>
  );
};

export default ManageJobDescriptionPage;
